import {Injectable} from '@angular/core';
import {EnvService} from '../../env-service/env.service';
import {ApplicationInsights, DistributedTracingModes} from "@microsoft/applicationinsights-web";
import {AngularPlugin} from '@microsoft/applicationinsights-angularplugin-js';
import {Router} from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AppInsightsApiService {
  //SRE
  appInsights: ApplicationInsights;

  constructor(private envService: EnvService, private router: Router) {
    const angularPlugin = new AngularPlugin();
    this.appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: this.envService.sreAPPIKey,
        connectionString: this.envService.sreAPPIConnectionString,
        autoTrackPageVisitTime: true, // On a pageview,the previous instrumented page's view time is tracked and sent as telemetry and a new timer is started for the current pageview.
        enableAutoRouteTracking: true, // Automatically track route changes in Single Page Applications (SPA).
        distributedTracingMode: DistributedTracingModes.W3C,
        enableAjaxErrorStatusText: true,
        //overridePageViewDuration: true,
        extensions: [angularPlugin],
        extensionConfig: {
          [angularPlugin.identifier]: { router: this.router },
        },
        //enableCorsCorrelation: true, //SDK will add two headers ('Request-Id' and 'Request-Context') to all CORS requests for correlation
      },
    });
    // Initialize this instance of ApplicationInsights
    this.appInsights.loadAppInsights();
    this.appInsights.addTelemetryInitializer((envelope) => {
      envelope.tags['ai.cloud.role'] = 'amu-eoi';
      envelope.tags['ai.cloud.roleInstance'] = this.envService.environment;
    });
  }

  /**
   * The following methods enables the delivery team to incrementally further instrument
   * the app based on priorities. You can consider to move this forward or remove the code as you see fit.
   * They are only wrappers around methods exposed by Application Insights to manually trigger telemetry collection as necessary.
   */

  /**
   * Methods to Start and Stop tracking pageView for the given URL
   * @param pageName
   */
  startNavigationEvent(pageName: string) {
    this.appInsights.startTrackPage(pageName);
  }

  endNavigationEvent(pageName: string) {
    this.appInsights.stopTrackPage(pageName);
  }

  /**
   * Logs that a page was displayed to the user (manual option)
   * @param name
   * @param url
   */
  logPageView(name?: string, url?: string) {
    // option to call manually
    this.appInsights.trackPageView({
      name: name,
      uri: url,
    });
  }

  /**
   * Log a user action
   * @param name
   * @param properties
   */
  logEvent(name: string, properties?: { [key: string]: any }) {
    this.appInsights.trackEvent({name: name}, properties);
  }

  /**
   * Log a numeric value that is not associated with a specific event.
   * Typically used to send regular reports of performance indicators.
   * @param name
   * @param average
   * @param properties
   */
  logMetric(
    name: string,
    average: number,
    properties?: { [key: string]: any }
  ) {
    this.appInsights.trackMetric({name: name, average: average}, properties);
  }

  /**
   * Log an exception that you have caught.
   * @param exception
   * @param severityLevel
   */
  logException(exception: Error, severityLevel?: number) {
    this.appInsights.trackException({
      exception: exception,
      severityLevel: severityLevel,
    });
  }

  /**
   * Log a diagnostic scenario such entering or leaving a function.
   * @param message
   * @param properties
   */
  logTrace(message: string, properties?: { [key: string]: any }) {
    this.appInsights.trackTrace({message: message}, properties);
  }
}
